import { useDialog } from 'muibox';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export namespace OnlyFansStatisticsEarningsOverviewAndChartData {
    export interface Response {
        total: ChartData;
        subscribes: ChartData;
        tips: ChartData;
        tips_stream: ChartData;
        post: ChartData;
        chat_messages: ChartData;
        ref: ChartData;
        stream: ChartData;
    }

    export interface ChartData {
        delta: number;
        chartAmount: ChartAmount[];
        total: number;
        gross: number;
    }

    export interface ChartAmount {
        date: Date;
        count: number;
    }
}

type EarningsOverallAllQuery = {
    mode?: 'all' | 'range' | 'since';
    startDate?: Date;
    endDate?: Date;
    sinceDate?: Date;
};

const useSubscriberStatsEarningsOverallAll = (subscriberId: string | undefined, query?: EarningsOverallAllQuery) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const dialog = useDialog();
    const axios = useAxios();

    const mode = query?.mode || 'all';

    const fetchData = async (): Promise<OnlyFansStatisticsEarningsOverviewAndChartData.Response | null> => {
        if (!userContext.jwtToken || !settingsContext.apiKey || !subscriberId) {
            return null;
        }

        const search = new URLSearchParams();
        search.set('mode', mode);
        if (query?.startDate) {
            search.set('startDate', query.startDate.toISOString());
        }
        if (query?.endDate) {
            search.set('endDate', query.endDate.toISOString());
        }
        if (query?.sinceDate) {
            search.set('sinceDate', query.sinceDate.toISOString());
        }
        const queryUrl = `${settingsContext.routes.onlyFans.base}${subscriberId}/stats/earnings/overall/all?${search.toString()}`;

        return axios
            .get(queryUrl)
            .then(response => response.data as OnlyFansStatisticsEarningsOverviewAndChartData.Response)
            .catch(error => {
                handleHttpError(error, dialog);

                return null;
            });
    };

    return useQuery(
        ['subscriberStatsEarningsOverallAll', subscriberId, mode, query?.startDate, query?.endDate, query?.sinceDate],
        fetchData,
        {
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 5, // 5 minutes
            enabled: !!(userContext.jwtToken && settingsContext.apiKey && subscriberId),
        },
    );
};

export default useSubscriberStatsEarningsOverallAll;
